import React, { useEffect, useRef, useState } from "react";
import { IMAGES, PARK } from "@/lib/site";
import { ArrowDown, ChevronUp, Play, X, MapPin } from "lucide-react";
import { useContent } from "@/lib/content";

const DEFAULT_HERO_VIDEO =
  "https://customer-assets.emergentagent.com/job_scenic-retreat-2/artifacts/xp7rjym7_petal_20260706_042440.mp4";

const Hero = () => {
  const c = useContent();
  const heroImage = c("hero.image") || IMAGES.hero;
  const [tourOpen, setTourOpen] = useState(false);
  const tourVideoRef = useRef(null);
  const tourUrl = c("hero.video", DEFAULT_HERO_VIDEO);

  // Close tour on Escape
  useEffect(() => {
    if (!tourOpen) return;
    const onKey = (e) => e.key === "Escape" && setTourOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [tourOpen]);

  return (
    <>
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden grain"
    >
      {/* Visually hidden H1 for SEO / screen readers — the visible brand mark lives in the navbar */}
      <h1 className="sr-only">
        Walnut Lakes Holiday Park — adults-only lakeside lodges, camping pitches and private carp fishing in Lincolnshire
      </h1>
      {/* Background — hero image kept clear with only minimal edge shading */}
      <div className="absolute inset-0 bg-[#050505]">
        {/* Slow-zoom hero photo */}
        <div
          className="absolute inset-0 bg-no-repeat bg-center bg-cover slow-zoom"
          style={{ backgroundImage: `url(${heroImage})` }}
          data-testid="hero-image"
          aria-hidden
        />
        {/* Subtle top shade so the navbar stays legible */}
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#050505]/70 to-transparent pointer-events-none" />
        {/* Subtle bottom shade so the CTAs stay legible without covering the image */}
        <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#050505]/80 via-[#050505]/25 to-transparent pointer-events-none" />
      </div>

      {/* Content: CTAs anchored at the bottom, plenty of breathing room, no full-image overlay */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 md:px-16 min-h-screen flex flex-col justify-end pt-32 md:pt-40 pb-24 md:pb-28">
        <div className="fade-up delay-400 flex flex-col md:flex-row items-stretch md:items-center gap-4 md:gap-10">
          {/* Mobile: Book Your Stay full-width, Enquire + Site Tour in a 2-col row below.
              Desktop: all three inline. */}
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 w-full md:w-auto">
            <a
              href="#book"
              data-testid="hero-book-cta"
              className="btn-luxe-xl btn-compact-mobile font-brand tracking-[0.18em] text-center whitespace-nowrap w-full md:w-auto"
            >
              {c("hero.cta_primary", "Book your stay")}
            </a>
            <div className="grid grid-cols-2 gap-3 md:flex md:flex-row md:items-center md:gap-5">
              <a
                href="#contact"
                data-testid="hero-enquire-cta"
                className="btn-ghost-luxe-xl btn-compact-mobile font-brand tracking-[0.18em] text-center whitespace-nowrap"
              >
                {c("hero.cta_secondary", "Enquire")}
              </a>
              {tourUrl && (
                <button
                  type="button"
                  onClick={() => setTourOpen(true)}
                  data-testid="hero-site-tour-cta"
                  className="btn-ghost-luxe-xl btn-compact-mobile font-brand tracking-[0.18em] text-center whitespace-nowrap inline-flex items-center justify-center gap-2 group"
                >
                  <Play size={12} fill="currentColor" className="transition-transform duration-300 group-hover:scale-125" />
                  Site Tour
                </button>
              )}
            </div>
          </div>
          {c("pitchmap.enabled") === "1" && (
            <a
              href="/pick-your-pitch"
              data-testid="hero-pick-pitch-cta"
              className="btn-luxe-xl btn-compact-mobile font-brand tracking-[0.18em] text-center whitespace-nowrap w-full md:w-auto inline-flex items-center justify-center gap-2"
            >
              <MapPin size={14} /> Pick your exact pitch
            </a>
          )}
          <div className="flex flex-row flex-wrap items-center gap-x-4 gap-y-1 md:gap-6 text-[#F5EFE6]/85 text-[10px] md:text-xs uppercase tracking-[0.24em] md:tracking-[0.28em] md:ml-auto mt-1 md:mt-0">
            <span>{PARK.season}</span>
            <span>Adults only · 15+</span>
            <span>Dog friendly</span>
          </div>
        </div>

        {/* Mobile-only swipe-up hint — nudges visitors to explore below the fold */}
        <a
          href="#about"
          data-testid="hero-swipe-hint"
          aria-label="Scroll down to explore"
          className="md:hidden swipe-hint-fade mt-6 self-center flex flex-col items-center gap-1 text-[#F5EFE6]/80 text-[10px] uppercase tracking-[0.32em]"
        >
          <ChevronUp size={16} className="swipe-hint-bounce" strokeWidth={1.5} />
          <span>Swipe up to explore</span>
        </a>
      </div>
    </section>

    {/* Site-tour video lightbox */}
    {tourOpen && (
      <div
        data-testid="site-tour-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Walnut Lakes site tour video"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md fade-up"
        onClick={() => setTourOpen(false)}
      >
        <button
          type="button"
          onClick={() => setTourOpen(false)}
          data-testid="site-tour-close"
          aria-label="Close site tour"
          className="absolute top-5 right-5 md:top-8 md:right-8 z-10 p-3 rounded-full bg-white/10 border border-white/20 text-white hover:bg-[#B76E79] hover:border-[#B76E79] transition-all"
        >
          <X size={22} />
        </button>
        <div
          className="relative w-[95vw] max-w-6xl aspect-video rounded-xl overflow-hidden shadow-[0_30px_80px_rgba(183,110,121,0.25)] border border-[#B76E79]/30"
          onClick={(e) => e.stopPropagation()}
        >
          <video
            ref={tourVideoRef}
            data-testid="site-tour-video"
            src={tourUrl}
            controls
            autoPlay
            playsInline
            onLoadedMetadata={(e) => { e.currentTarget.playbackRate = 3; }}
            className="w-full h-full object-cover bg-black"
          />
        </div>
        <p className="absolute bottom-6 md:bottom-10 left-0 right-0 text-center text-[#E4B2A8] text-[11px] md:text-xs uppercase tracking-[0.35em] font-brand pointer-events-none">
          Walnut Lakes · A view from above
        </p>
      </div>
    )}
    </>
  );
};


export default Hero;
